import { Organization, TenantGeneral, TenantRole } from 'api/endpoints/tenants';
import { WithTenantId } from 'api/types/pathParams';
import { TENANT_ROLE, USER_ROLE, UserRole } from 'constants/roles';

import { API_REQ_ACTION_TYPES } from '../actions';
import apiReqSagaCreator from '../apiReqSagaCreator';
import { ApiPaths, HTTP_METHODS } from '../constants';

export interface UpdateUserDetails {
  data: Pick<User, 'firstName' | 'lastName' | 'role' | 'phone' | 'email'>;
}

export interface UserTenant extends Pick<BaseEntity, 'id' | 'name'> {
  userRole: TenantRole | 'admin';
}

export enum UserStatus {
  Active = 'active',
  Blocked = 'blocked',
}

export interface UserInfoTenant {
  tenant: TenantGeneral;
  permissions: SystemUserPermissions;
  userRole: TenantRole;
}

export type UserProfile = Pick<
  AbsoluteBaseEntity,
  'createdAt' | 'updatedAt'
> & {
  currentTenant: BaseEntity;
};

export interface User extends AbsoluteBaseEntity, WithHypermedia {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  username: string;
  organization: Organization;
  tenants?: UserInfoTenant[];
  // groups: (unknown security group type)
  profile: UserProfile;
  status: UserStatus;
  role: UserRole;
  acceptedTermsAt: Nullable<Date>;
  acceptedPrivacyPolicyAt: Nullable<Date>;
}

export const isUserAdmin = (user: User): boolean =>
  user.role === UserRole.Admin;
const userCurrentTenantId = (user: User) => user.profile.currentTenant.id;
export const userCurrentTenant = (user: User) => {
  const currentTenantId = userCurrentTenantId(user);
  return user.tenants?.find((tenant) => tenant.tenant.id === currentTenantId);
};
export const isUserTenantAdmin = (user: User) =>
  !isUserAdmin(user) && userCurrentTenant(user)?.userRole === TENANT_ROLE.ADMIN;
const isUserTenantManager = (user: User) =>
  !isUserAdmin(user) &&
  userCurrentTenant(user)?.userRole === TENANT_ROLE.MANAGER;

export function currentTenantRole(user: User): TenantRole | UserRole.Admin {
  if (isUserTenantAdmin(user)) {
    return TENANT_ROLE.ADMIN;
  } else if (isUserAdmin(user)) {
    return USER_ROLE.ADMIN;
  } else if (isUserTenantManager(user)) {
    return TENANT_ROLE.MANAGER;
  } else {
    return TENANT_ROLE.USER;
  }
}

export interface UserWithIdentityProvider extends User {
  excludedFromIdentityProvider: boolean;
  linkedToIdentityProvider: boolean;
}

export interface UserWithIdentityProviderAndLastAccessed extends UserWithIdentityProvider {
  lastAccessed?: string;
}

export interface SystemUserPermissions {
  allowDeleteInstance: boolean;
  allowUpdateVariables: boolean;
  allowReprocessInstance: boolean;
  allowStartStopPolling: boolean;
}

export interface UserTenantRolesMap {
  [tenantId: string]: TenantRole;
}
type UpdateUserActiveTenantParams = WithTenantId;
export type UpdateUserActiveTenantResult = UserProfile;

const Users = {
  updateUserActiveTenant: apiReqSagaCreator<
    UpdateUserActiveTenantParams,
    UpdateUserActiveTenantResult
  >(API_REQ_ACTION_TYPES.USERS.UPDATE_USER_ACTIVE_TENANT, (args) => ({
    method: HTTP_METHODS.PUT,
    url: ApiPaths.users.profile._(),
    data: {
      currentTenantId: args.tenantId,
    },
  })),
};

export default Users;
